import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUiLang } from '../utils/lang';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ShoppingCart, Filter, X, Search, SlidersHorizontal, Check, Lock, ChevronRight, Package, Folder, ArrowLeft, Heart, Scale, FilterX } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import StarRating from '../components/StarRating';
import { useWishlist } from '../context/WishlistContext';
import { useCompare } from '../context/CompareContext';
import debounce from 'lodash.debounce';
import SEO from '../components/SEO';
import StructuredData, { buildBreadcrumbSchema } from '../components/StructuredData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default images for categories
const defaultImages = {
  'exterior': 'https://customer-assets.emergentagent.com/job_sunprotect/artifacts/mx206kbm_in%C5%A1tal%C3%A1cia%20tepelnoizola%C4%8Dnej%20f%C3%B3lie-2.webp',
  'interior': 'https://customer-assets.emergentagent.com/job_sunprotect/artifacts/96b1auzw_4.NSN70_1000x1000.webp',
  'architectural': 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80',
  'automotive': 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800&q=80',
  'tools': 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=800&q=80',
  'materials': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80',
  'default': 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80'
};

const Catalog = () => {
  const { t, i18n } = useTranslation();
  const { addToCart, currency } = useCart();
  const { isAuthenticated } = useAuth();
  const { isInWishlist, toggle: toggleWishlist } = useWishlist();
  const { has: hasCompare, toggle: toggleCompare } = useCompare();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  
  // Categories state
  const [allCategories, setAllCategories] = useState([]);
  const [currentCategoryData, setCurrentCategoryData] = useState(null);
  const [childCategories, setChildCategories] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [showCategoryCards, setShowCategoryCards] = useState(false);
  const [categoryCounts, setCategoryCounts] = useState({});
  
  // Search & Filters
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedBrands, setSelectedBrands] = useState(
    searchParams.get('brands')?.split(',').filter(Boolean) || []
  );
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [priceRange, setPriceRange] = useState({
    min: parseFloat(searchParams.get('min_price')) || 0,
    max: parseFloat(searchParams.get('max_price')) || 500
  });
  const [inStock, setInStock] = useState(searchParams.get('in_stock') === 'true');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'name');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
  
  // Filter options from API
  const [filterOptions, setFilterOptions] = useState({
    brands: [],
    categories: [],
    price_range: { min: 0, max: 500 }
  });
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (searchQuery) params.append('q', searchQuery);
      if (selectedCategory) params.append('category_id', selectedCategory);
      if (selectedBrands.length > 0) params.append('brands', selectedBrands.join(','));
      if (priceRange.min > 0) params.append('min_price', priceRange.min);
      if (priceRange.max < filterOptions.price_range.max) params.append('max_price', priceRange.max);
      if (inStock) params.append('in_stock', 'true');
      params.append('sort_by', sortBy);
      params.append('page', currentPage);
      params.append('per_page', 20);

      const response = await axios.get(`${API}/products/search/advanced?${params}`);
      
      setProducts(response.data.products);
      setPagination(response.data.pagination);
      setFilterOptions(response.data.filters);
      
      // Update URL
      const newParams = new URLSearchParams();
      if (searchQuery) newParams.set('q', searchQuery);
      if (selectedCategory) newParams.set('category', selectedCategory);
      if (selectedBrands.length > 0) newParams.set('brands', selectedBrands.join(','));
      if (priceRange.min > 0) newParams.set('min_price', priceRange.min);
      if (priceRange.max < 500) newParams.set('max_price', priceRange.max);
      if (inStock) newParams.set('in_stock', 'true');
      if (sortBy !== 'name') newParams.set('sort', sortBy);
      if (currentPage > 1) newParams.set('page', currentPage);
      setSearchParams(newParams);
      
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedCategory, selectedBrands, priceRange, inStock, sortBy, currentPage]);

  // Debounced search
  const debouncedFetch = useCallback(
    debounce(() => fetchProducts(), 300),
    [fetchProducts]
  );

  useEffect(() => {
    debouncedFetch();
    return () => debouncedFetch.cancel();
  }, [searchQuery, selectedCategory, selectedBrands, priceRange, inStock, sortBy, currentPage]);

  const getTranslation = (obj, field) => {
    return obj?.translations?.[getUiLang(i18n)]?.[field] || obj?.translations?.['sk']?.[field] || '';
  };

  // Fetch all categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const [categoriesRes, countsRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/categories/counts/all`)
        ]);
        setAllCategories(categoriesRes.data);
        setCategoryCounts(countsRes.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Update category view when selectedCategory or allCategories changes
  useEffect(() => {
    if (allCategories.length === 0) return;
    
    if (selectedCategory) {
      const current = allCategories.find(c => c.id === selectedCategory);
      setCurrentCategoryData(current);
      
      // Find children of selected category
      const children = allCategories.filter(c => c.parent_id === selectedCategory);
      setChildCategories(children);
      
      // Show category cards if this category has children AND no search query
      setShowCategoryCards(children.length > 0 && !searchQuery);
      
      // Build breadcrumbs
      const crumbs = [];
      let cat = current;
      while (cat) {
        crumbs.unshift(cat);
        cat = allCategories.find(c => c.id === cat.parent_id);
      }
      setBreadcrumbs(crumbs);
    } else {
      // No category selected - show root categories
      setCurrentCategoryData(null);
      const rootCategories = allCategories.filter(c => 
        c.parent_id === null || c.parent_id === undefined || c.parent_id === ''
      );
      setChildCategories(rootCategories);
      // Show category cards only if no search query
      setShowCategoryCards(rootCategories.length > 0 && !searchQuery);
      setBreadcrumbs([]);
    }
  }, [selectedCategory, allCategories, searchQuery]);

  // Get category image
  const getCategoryImage = (category) => {
    if (category.image_url) return category.image_url;
    
    const name = getTranslation(category, 'name').toLowerCase();
    const slug = (category.slug || '').toLowerCase();
    
    if (slug.includes('exterier') || name.includes('exteriér')) return defaultImages.exterior;
    if (slug.includes('interier') || name.includes('interiér')) return defaultImages.interior;
    if (slug.includes('architekt') || name.includes('architekt')) return defaultImages.architectural;
    if (slug.includes('auto') || name.includes('auto')) return defaultImages.automotive;
    if (slug.includes('nastroj') || name.includes('nástroj')) return defaultImages.tools;
    if (slug.includes('montaz') || name.includes('montáž')) return defaultImages.materials;
    
    return defaultImages.default;
  };

  // Handle category card click
  const handleCategoryClick = (category) => {
    const children = allCategories.filter(c => c.parent_id === category.id);
    if (children.length > 0) {
      // Has subcategories - show category cards
      setSelectedCategory(category.id);
      setShowCategoryCards(true);  // Show category cards!
      setCurrentPage(1);
      setSearchParams({ category: category.id });
    } else {
      // No subcategories - show products
      setSelectedCategory(category.id);
      setShowCategoryCards(false);
      setCurrentPage(1);
      setSearchParams({ category: category.id });
    }
  };

  // Go back to parent category
  const handleBackClick = () => {
    if (currentCategoryData?.parent_id) {
      setSelectedCategory(currentCategoryData.parent_id);
      setSearchParams({ category: currentCategoryData.parent_id });
    } else {
      setSelectedCategory('');
      setSearchParams({});
    }
    setCurrentPage(1);
  };

  // Grid columns based on number of items
  const getGridCols = (count) => {
    if (count === 1) return 'grid-cols-1 max-w-2xl mx-auto';
    if (count === 2) return 'grid-cols-1 md:grid-cols-2 max-w-5xl mx-auto';
    if (count === 3) return 'grid-cols-1 md:grid-cols-3';
    return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';
  };

  const toggleBrand = (brandId) => {
    setSelectedBrands(prev => 
      prev.includes(brandId) 
        ? prev.filter(id => id !== brandId)
        : [...prev, brandId]
    );
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedBrands([]);
    setSelectedCategory('');
    setPriceRange({ min: 0, max: filterOptions.price_range.max || 500 });
    setInStock(false);
    setSortBy('name');
    setCurrentPage(1);
    setSearchParams({});
  };

  const hasActiveFilters = searchQuery || selectedBrands.length > 0 || selectedCategory || 
    priceRange.min > 0 || priceRange.max < (filterOptions.price_range.max || 500) || inStock;

  const getPrice = (product) => {
    return currency === 'EUR' ? product.price_eur : product.price_usd;
  };

  const formatPrice = (price) => {
    return currency === 'EUR' ? `€${price?.toFixed(2)}` : `$${price?.toFixed(2)}`;
  };

  // SEO meta data by language
  const seoData = {
    sk: {
      title: currentCategoryData ? getTranslation(currentCategoryData, 'name') : 'Katalóg',
      description: currentCategoryData 
        ? `${getTranslation(currentCategoryData, 'name')} - slnečné ochranné fólie. Široký výber fólií od značiek Armolan, Madico, Solar Gard, Bruxsafol, LLumar.`
        : 'Katalóg slnečných ochranných fólií na okná. Architektonické fólie, autofólie, nástroje na montáž. Armolan, Madico, Solar Gard, Bruxsafol, LLumar.',
      keywords: 'katalóg fólie, slnečné fólie, architektonické fólie, autofólie, montážne nástroje'
    },
    cs: {
      title: currentCategoryData ? getTranslation(currentCategoryData, 'name') : 'Katalog',
      description: 'Katalog slunečních ochranných fólií na okna. Architektonické fólie, autofólie, montážní nástroje.',
      keywords: 'katalog fólie, sluneční fólie, architektonické fólie'
    },
    en: {
      title: currentCategoryData ? getTranslation(currentCategoryData, 'name') : 'Catalog',
      description: 'Window film catalog. Architectural films, automotive films, installation tools. Armolan, Madico, Solar Gard, Bruxsafol, LLumar.',
      keywords: 'film catalog, window films, architectural films, automotive films, installation tools'
    },
    de: {
      title: currentCategoryData ? getTranslation(currentCategoryData, 'name') : 'Katalog',
      description: 'Katalog der Sonnenschutzfolien. Architekturfolien, Autofolien, Montagewerkzeuge.',
      keywords: 'Folien Katalog, Sonnenschutzfolien, Architekturfolien, Autofolien'
    },
    ru: {
      title: currentCategoryData ? getTranslation(currentCategoryData, 'name') : 'Каталог',
      description: 'Каталог солнцезащитных пленок для окон. Архитектурные пленки, автопленки, инструменты для монтажа.',
      keywords: 'каталог пленок, солнцезащитные пленки, архитектурные пленки, автопленки'
    }
  };

  const currentSeo = seoData[i18n.language] || seoData.sk;

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title={currentSeo.title}
        description={currentSeo.description}
        keywords={currentSeo.keywords}
        url="/catalog"
      />
      <StructuredData data={buildBreadcrumbSchema([
        { name: 'Sunify', url: '/' },
        { name: t('nav.catalog', 'Katalóg'), url: '/catalog' },
        ...((breadcrumbs || []).map(b => ({ name: b.name, url: `/catalog/category/${b.id}` }))),
      ], { siteUrl: typeof window !== 'undefined' ? window.location.origin : '' })} />
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-yellow-400 text-white py-12">
        <div className="container mx-auto px-4">
          {/* Breadcrumbs */}
          {breadcrumbs.length > 0 && (
            <nav className="flex items-center gap-2 text-white/80 mb-4 text-sm">
              <button 
                onClick={() => { setSelectedCategory(''); setSearchParams({}); }}
                className="hover:text-white"
              >
                {t('nav.catalog')}
              </button>
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={crumb.id}>
                  <ChevronRight className="h-4 w-4" />
                  {index === breadcrumbs.length - 1 ? (
                    <span className="text-white font-medium">
                      {getTranslation(crumb, 'name')}
                    </span>
                  ) : (
                    <button 
                      onClick={() => { 
                        setSelectedCategory(crumb.id); 
                        setSearchParams({ category: crumb.id }); 
                      }}
                      className="hover:text-white"
                    >
                      {getTranslation(crumb, 'name')}
                    </button>
                  )}
                </React.Fragment>
              ))}
            </nav>
          )}
          
          <h1 className="text-4xl font-bold mb-4" data-testid="catalog-title">
            {currentCategoryData ? getTranslation(currentCategoryData, 'name') : t('nav.catalog')}
          </h1>
          
          {currentCategoryData && getTranslation(currentCategoryData, 'description') && (
            <p className="text-white/90 max-w-2xl mb-4">
              {getTranslation(currentCategoryData, 'description')}
            </p>
          )}
          
          {/* Search Bar - only show when viewing products */}
          {!showCategoryCards && (
            <div className="max-w-2xl">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder={t('common.search') + '...'}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="pl-12 pr-4 py-3 w-full text-gray-800 rounded-xl border-0 shadow-lg"
                  data-testid="search-input"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Category Cards View */}
        {showCategoryCards && childCategories.length > 0 ? (
          <div>
            {/* Back button */}
            {currentCategoryData && (
              <button
                onClick={handleBackClick}
                className="flex items-center gap-2 text-gray-600 hover:text-orange-500 mb-6 transition"
              >
                <ArrowLeft className="h-5 w-5" />
                {t('common.back')}
              </button>
            )}
            
            {/* Category Cards Grid */}
            <div className={`grid gap-6 ${getGridCols(childCategories.length)}`}>
              {childCategories.map((category) => {
                const childCount = allCategories.filter(c => c.parent_id === category.id).length;
                const hasSubcategories = childCount > 0;
                const imageUrl = getCategoryImage(category);
                const productCount = categoryCounts[category.id] || 0;
                
                return (
                  <div
                    key={category.id}
                    onClick={() => handleCategoryClick(category)}
                    className="group cursor-pointer"
                    data-testid={`category-card-${category.id}`}
                  >
                    <div className={`
                      relative overflow-hidden rounded-2xl shadow-lg 
                      ${childCategories.length <= 2 ? 'h-[350px]' : 'h-[280px]'}
                      transform transition-all duration-500 ease-out
                      hover:scale-[1.02] hover:shadow-2xl
                    `}>
                      {/* Background Image */}
                      <div 
                        className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                        style={{ backgroundImage: `url(${imageUrl})` }}
                      />
                      
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                      
                      {/* Hover Overlay */}
                      <div className="absolute inset-0 bg-orange-500/0 group-hover:bg-orange-500/20 transition-colors duration-300" />
                      
                      {/* Product Count Badge */}
                      {productCount > 0 && (
                        <div className="absolute top-4 right-4 bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                          {productCount}
                        </div>
                      )}
                      
                      {/* Content */}
                      <div className="absolute inset-0 flex flex-col justify-end p-6">
                        <h3 className="text-white text-2xl md:text-3xl font-bold mb-2 transform transition-transform duration-300 group-hover:translate-x-2">
                          {getTranslation(category, 'name')}
                        </h3>
                        
                        {getTranslation(category, 'description') && (
                          <p className="text-white/80 text-sm mb-3 line-clamp-2">
                            {getTranslation(category, 'description')}
                          </p>
                        )}
                        
                        <div className="flex items-center justify-between">
                          {hasSubcategories ? (
                            <p className="text-white/80 text-sm flex items-center gap-2">
                              <Folder className="h-4 w-4" />
                              {childCount} {t('catalog.subcategories')}
                              {productCount > 0 && (
                                <span className="ml-2 text-orange-300">
                                  ({productCount} {t('catalog.products_count')})
                                </span>
                              )}
                            </p>
                          ) : (
                            <p className="text-white/80 text-sm flex items-center gap-2">
                              <Package className="h-4 w-4" />
                              {productCount > 0 ? (
                                <span>{productCount} {t('catalog.products_count')}</span>
                              ) : (
                                t('catalog.view_products')
                              )}
                            </p>
                          )}
                          
                          <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center transform transition-all duration-300 group-hover:bg-orange-500 group-hover:translate-x-1">
                            <ChevronRight className="h-5 w-5 text-white" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* View all products button when on root */}
            {!currentCategoryData && (
              <div className="mt-8 text-center">
                <button
                  onClick={() => setShowCategoryCards(false)}
                  className="inline-flex items-center gap-2 text-orange-500 hover:text-orange-600 font-medium"
                >
                  {t('catalog.view_all_products')}
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Back button when viewing products */}
            {currentCategoryData && (
              <button
                onClick={handleBackClick}
                className="flex items-center gap-2 text-gray-600 hover:text-orange-500 mb-6 transition"
              >
                <ArrowLeft className="h-5 w-5" />
                {t('common.back')}
              </button>
            )}
            
            {/* Results Header */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="lg:hidden flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow"
                >
                  <SlidersHorizontal className="h-5 w-5" />
                  {t('common.filter')}
                </button>
            
            <p className="text-gray-600">
              {loading ? t('common.loading') : (
                <span>{pagination.total} {t('common.products')}</span>
              )}
            </p>
            
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-orange-500 hover:text-orange-600 text-sm flex items-center gap-1"
              >
                <X className="h-4 w-4" />
                {t('common.clear')}
              </button>
            )}
          </div>

          {/* Sort */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">{t('common.sort')}:</span>
            <select
              value={sortBy}
              onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }}
              className="px-3 py-2 border rounded-lg text-sm bg-white"
              data-testid="sort-select"
            >
              <option value="name">{t('catalog.sort_name')}</option>
              <option value="price_asc">{t('catalog.sort_price_asc')}</option>
              <option value="price_desc">{t('catalog.sort_price_desc')}</option>
              <option value="newest">{t('catalog.sort_newest')}</option>
            </select>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <aside className={`${showFilters ? 'block' : 'hidden'} lg:block w-full lg:w-72 flex-shrink-0`}>
            <div className="bg-white rounded-xl shadow-md p-6 sticky top-24">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <Filter className="h-5 w-5" />
                  {t('common.filter')}
                </h2>
                <button
                  onClick={() => setShowFilters(false)}
                  className="lg:hidden"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Category Filter */}
              <div className="mb-6">
                <h3 className="font-semibold mb-3">{t('categories.title')}</h3>
                <div className="space-y-1">
                  <button
                    onClick={() => { setSelectedCategory(''); setSearchParams({}); setCurrentPage(1); }}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition ${
                      !selectedCategory ? 'bg-orange-100 text-orange-600' : 'hover:bg-gray-100'
                    }`}
                  >
                    {t('catalog.all_categories')}
                  </button>
                  {/* Hierarchical categories */}
                  {filterOptions.categories
                    .filter(cat => !cat.parent_id)
                    .map(parentCat => (
                      <div key={parentCat.id}>
                        <button
                          onClick={() => handleCategoryClick(parentCat)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition ${
                            selectedCategory === parentCat.id ? 'bg-orange-100 text-orange-600' : 'hover:bg-gray-100'
                          }`}
                          data-testid={`filter-category-${parentCat.id}`}
                        >
                          {getTranslation(parentCat, 'name')}
                        </button>
                        {/* Subcategories level 1 */}
                        {filterOptions.categories
                          .filter(sub => sub.parent_id === parentCat.id)
                          .map(subCat => (
                            <div key={subCat.id}>
                              <button
                                onClick={() => handleCategoryClick(subCat)}
                                className={`w-full text-left pl-6 pr-3 py-1.5 rounded-lg text-sm transition ${
                                  selectedCategory === subCat.id ? 'bg-orange-100 text-orange-600' : 'hover:bg-gray-100 text-gray-700'
                                }`}
                                data-testid={`filter-category-${subCat.id}`}
                              >
                                └ {getTranslation(subCat, 'name')}
                              </button>
                              {/* Subcategories level 2 */}
                              {filterOptions.categories
                                .filter(sub2 => sub2.parent_id === subCat.id)
                                .map(subCat2 => (
                                  <button
                                    key={subCat2.id}
                                    onClick={() => handleCategoryClick(subCat2)}
                                    className={`w-full text-left pl-10 pr-3 py-1 rounded-lg text-xs transition ${
                                      selectedCategory === subCat2.id ? 'bg-orange-100 text-orange-600' : 'hover:bg-gray-100 text-gray-600'
                                    }`}
                                    data-testid={`filter-category-${subCat2.id}`}
                                  >
                                    • {getTranslation(subCat2, 'name')}
                                  </button>
                                ))}
                            </div>
                          ))}
                      </div>
                    ))}
                </div>
              </div>

              {/* Brand Filter */}
              <div className="mb-6">
                <h3 className="font-semibold mb-3">{t('nav.brands')}</h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {filterOptions.brands.map(brand => (
                    <label 
                      key={brand.id} 
                      className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-gray-50"
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition ${
                        selectedBrands.includes(brand.id) 
                          ? 'bg-orange-500 border-orange-500' 
                          : 'border-gray-300'
                      }`}>
                        {selectedBrands.includes(brand.id) && (
                          <Check className="h-3 w-3 text-white" />
                        )}
                      </div>
                      <span className="text-sm">{brand.name}</span>
                      <input
                        type="checkbox"
                        checked={selectedBrands.includes(brand.id)}
                        onChange={() => toggleBrand(brand.id)}
                        className="hidden"
                        data-testid={`filter-brand-${brand.id}`}
                      />
                    </label>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div className="mb-6">
                <h3 className="font-semibold mb-3">{t('catalog.price_range')}</h3>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    value={priceRange.min}
                    onChange={(e) => { 
                      setPriceRange(prev => ({ ...prev, min: parseFloat(e.target.value) || 0 }));
                      setCurrentPage(1);
                    }}
                    className="w-24 text-sm"
                    placeholder="Min"
                    data-testid="price-min"
                  />
                  <span className="text-gray-400">—</span>
                  <Input
                    type="number"
                    min="0"
                    value={priceRange.max}
                    onChange={(e) => { 
                      setPriceRange(prev => ({ ...prev, max: parseFloat(e.target.value) || 500 }));
                      setCurrentPage(1);
                    }}
                    className="w-24 text-sm"
                    placeholder="Max"
                    data-testid="price-max"
                  />
                  <span className="text-sm text-gray-500">€</span>
                </div>
              </div>

              {/* In Stock Filter */}
              <div className="mb-6">
                <label className="flex items-center gap-3 cursor-pointer">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition ${
                    inStock ? 'bg-orange-500 border-orange-500' : 'border-gray-300'
                  }`}>
                    {inStock && <Check className="h-3 w-3 text-white" />}
                  </div>
                  <span className="text-sm font-medium">{t('catalog.in_stock_only')}</span>
                  <input
                    type="checkbox"
                    checked={inStock}
                    onChange={(e) => { setInStock(e.target.checked); setCurrentPage(1); }}
                    className="hidden"
                    data-testid="filter-in-stock"
                  />
                </label>
              </div>

              {hasActiveFilters && (
                <Button
                  onClick={clearFilters}
                  variant="outline"
                  className="w-full"
                >
                  <X className="h-4 w-4 mr-2" />
                  {t('catalog.clear_filters')}
                </Button>
              )}
            </div>
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            {/* Beautiful Category Banner - always show when category is selected */}
            {currentCategoryData && !loading && (
              <div className="mb-8">
                <div className="relative overflow-hidden rounded-2xl shadow-xl h-[300px]">
                  {/* Background Image */}
                  <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${getCategoryImage(currentCategoryData)})` }}
                  />
                  
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                  
                  {/* Content */}
                  <div className="absolute inset-0 flex flex-col justify-end p-8">
                    <h2 className="text-white text-3xl font-bold mb-2">
                      {getTranslation(currentCategoryData, 'name')}
                    </h2>
                    
                    {getTranslation(currentCategoryData, 'description') && (
                      <p className="text-white/90 text-base max-w-2xl">
                        {getTranslation(currentCategoryData, 'description')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white rounded-xl shadow-md overflow-hidden animate-pulse">
                    <div className="aspect-square bg-gray-200" />
                    <div className="p-4 space-y-3">
                      <div className="h-4 bg-gray-200 rounded w-3/4" />
                      <div className="h-4 bg-gray-200 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12 bg-gray-100 rounded-xl" data-testid="catalog-empty-state">
                <div className="text-5xl mb-4">📦</div>
                {hasActiveFilters ? (
                  <>
                    <h3 className="text-xl font-semibold mb-2">
                      {t('catalog.no_products_filtered', 'Žiadne produkty pre vybrané filtre')}
                    </h3>
                    <p className="text-gray-500 mb-4">
                      {t('catalog.try_clearing_filters', 'Skúste zmeniť alebo zrušiť filtre.')}
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-2">
                      {selectedBrands.length > 0 && (
                        <Button
                          onClick={() => setSelectedBrands([])}
                          variant="outline"
                          data-testid="catalog-clear-brands"
                        >
                          <X className="h-4 w-4 mr-1.5" />
                          {t('catalog.clear_brands', 'Zrušiť filter značiek')} ({selectedBrands.length})
                        </Button>
                      )}
                      <Button
                        onClick={() => {
                          setSearchQuery('');
                          setSelectedBrands([]);
                          setSelectedCategory('');
                          setPriceRange({ min: '', max: '' });
                          setInStock(false);
                          setSearchParams({});
                        }}
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                        data-testid="catalog-clear-all-filters"
                      >
                        <FilterX className="h-4 w-4 mr-1.5" />
                        {t('catalog.clear_all_filters', 'Zrušiť všetky filtre')}
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <h3 className="text-xl font-semibold mb-2">{t('catalog.no_products_yet')}</h3>
                    <p className="text-gray-500 mb-4">{t('catalog.products_coming_soon')}</p>
                    <Button onClick={handleBackClick} variant="outline">
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      {t('common.back')}
                    </Button>
                  </>
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {products.map(product => (
                    <div 
                      key={product.id} 
                      className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition group relative"
                      data-testid={`product-card-${product.id}`}
                    >
                      {/* Quick actions: Wishlist + Compare */}
                      <div className="absolute top-3 right-3 z-10 flex flex-col gap-2">
                        {isAuthenticated && (
                          <button
                            onClick={(e) => { e.preventDefault(); toggleWishlist(product.id); }}
                            className={`p-2 rounded-full backdrop-blur transition shadow ${
                              isInWishlist(product.id)
                                ? 'bg-red-500 text-white'
                                : 'bg-white/90 text-gray-600 hover:bg-red-50 hover:text-red-500'
                            }`}
                            title={isInWishlist(product.id) ? 'In wishlist' : 'Add to wishlist'}
                            data-testid={`wishlist-toggle-${product.id}`}
                          >
                            <Heart className={`h-4 w-4 ${isInWishlist(product.id) ? 'fill-white' : ''}`} />
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.preventDefault(); toggleCompare(product.id); }}
                          className={`p-2 rounded-full backdrop-blur transition shadow ${
                            hasCompare(product.id)
                              ? 'bg-blue-500 text-white'
                              : 'bg-white/90 text-gray-600 hover:bg-blue-50 hover:text-blue-500'
                          }`}
                          title={hasCompare(product.id) ? 'In compare' : 'Add to compare'}
                          data-testid={`compare-toggle-${product.id}`}
                        >
                          <Scale className="h-4 w-4" />
                        </button>
                      </div>
                      <Link to={`/product/${product.id}`}>
                        <div className="aspect-square relative overflow-hidden">
                          <img
                            src={product.images?.[0] || 'https://via.placeholder.com/400'}
                            alt={getTranslation(product, 'name')}
                            className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                          />
                          {product.is_featured && (
                            <span className="absolute top-3 left-3 bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                              {t('featured.badge')}
                            </span>
                          )}
                          {product.stock === 0 && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                              <span className="bg-red-500 text-white px-4 py-2 rounded-lg font-semibold">
                                {t('product.on_order')}
                              </span>
                            </div>
                          )}
                          {product.stock === 1 && (
                            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                              <span className="bg-yellow-500 text-white px-4 py-2 rounded-lg font-semibold text-sm">
                                {t('product.check_with_seller_short')}
                              </span>
                            </div>
                          )}
                        </div>
                      </Link>
                      <div className="p-4">
                        <p className="text-sm text-orange-500 font-medium mb-1">{product.brand}</p>
                        <Link to={`/product/${product.id}`}>
                          <h3 className="font-semibold text-lg mb-2 hover:text-orange-500 transition line-clamp-2">
                            {getTranslation(product, 'name')}
                          </h3>
                        </Link>
                        {product.rating_count > 0 && (
                          <div className="mb-2" data-testid={`rating-${product.id}`}>
                            <StarRating value={product.rating_avg || 0} size="h-3.5 w-3.5" count={product.rating_count} />
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          {isAuthenticated ? (
                            <div>
                              <span className="text-xl font-bold text-gray-900">
                                {formatPrice(getPrice(product))}
                              </span>
                              <span className="text-xs text-gray-500 ml-1">/ {(product.unit || 'mb') === 'mb' ? t('product.unit_mb', 'bm') : t('product.pcs', 'ks')}</span>
                            </div>
                          ) : (
                            <span className="flex items-center gap-1 text-gray-500">
                              <Lock className="h-4 w-4" />
                              <span className="text-sm">{t('catalog.login_for_price')}</span>
                            </span>
                          )}
                          {product.stock > 1 && isAuthenticated && (
                            <button
                              onClick={() => addToCart(product, 1)}
                              className="p-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition"
                              data-testid={`add-to-cart-${product.id}`}
                            >
                              <ShoppingCart className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                        {product.stock > 1 && (
                          <p className="text-xs text-green-600 mt-2">
                            {product.unit === 'mb'
                              ? t('product.in_stock_mb', { count: product.stock })
                              : t('product.in_stock_pcs', { count: product.stock })}
                          </p>
                        )}
                        {product.stock >= 2 && product.stock <= 5 && (
                          <p className="text-xs text-orange-600 mt-1 font-medium" data-testid={`low-stock-${product.id}`}>
                            {product.unit === 'mb'
                              ? t('product.low_stock_warning_mb', { count: product.stock })
                              : t('product.low_stock_warning', { count: product.stock })}
                          </p>
                        )}
                        {product.stock === 1 && (
                          <p className="text-xs text-yellow-600 mt-2">
                            {t('product.check_with_seller')}
                          </p>
                        )}
                        {product.stock === 0 && (
                          <p className="text-xs text-red-500 mt-2">
                            {t('product.on_order')}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="flex justify-center gap-2 mt-8">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      ←
                    </Button>
                    {[...Array(Math.min(5, pagination.pages))].map((_, i) => {
                      let page;
                      if (pagination.pages <= 5) {
                        page = i + 1;
                      } else if (currentPage <= 3) {
                        page = i + 1;
                      } else if (currentPage >= pagination.pages - 2) {
                        page = pagination.pages - 4 + i;
                      } else {
                        page = currentPage - 2 + i;
                      }
                      return (
                        <Button
                          key={page}
                          variant={currentPage === page ? "default" : "outline"}
                          onClick={() => setCurrentPage(page)}
                          className={currentPage === page ? "bg-orange-500" : ""}
                        >
                          {page}
                        </Button>
                      );
                    })}
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage(p => Math.min(pagination.pages, p + 1))}
                      disabled={currentPage === pagination.pages}
                    >
                      →
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Catalog;
